import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

// Icons
import { RedDotIcon } from 'shared/assets/svg/menu-icons/RedDotIcon';

// Styles
import styles from './MenuItemButton.module.scss';
import { MenuItem } from './MenuItemButton.types';
import MenuItemButtonWrapper from './MenuItemButtonWrapper';

type MenuItemProps = {
  id?: string;
  item: MenuItem;
  active?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
  isExpandToggle?: boolean;
  showRedDot?: boolean;
  isTabletView?: boolean;
  showBubble?: boolean;
  handleHideBubble?: () => void;
  bubbleText?: string;
};

export const MenuItemButton: React.FC<MenuItemProps> = ({
  id,
  item,
  active,
  isDisabled = false,
  onClick,
  isExpandToggle = false,
  showRedDot = false,
  isTabletView = false,
  showBubble = false,
  handleHideBubble,
  bubbleText,
}) => {
  const { t } = useTranslation();
  const activeButton = active;
  const IconComponent = item.icon;
  const iconSize = item.iconSize || 24;

  const buttonClasses = classNames(styles.menuItem, {
    [styles.disabled]: isDisabled,
    [styles.tabletView]: isTabletView,
  });

  const textStyle = {
    color: activeButton ? item.activeIconColor : item.inactiveIconColor,
  };

  return (
    <MenuItemButtonWrapper item={item} isTabletView={isTabletView}>
      <div
        id={id}
        className={buttonClasses}
        style={{
          color: activeButton ? item.activeIconColor : null,
        }}
        onClick={() => {
          if (!isDisabled && onClick) {
            onClick();
          }
        }}
      >
        {isTabletView ? (
          // Tablet view with horizontal layout
          <div className={styles.tabletLayout}>
            <div className={styles.iconContainer}>
              <IconComponent size={iconSize} color={activeButton ? item.activeIconColor : item.inactiveIconColor} />
              {showRedDot && (
                <div className={styles.redDotContainer}>
                  <RedDotIcon small={false} />
                </div>
              )}
            </div>
            {!isExpandToggle && (
              <div className={styles.tabletLabel} style={textStyle}>
                {t(`menu.${item.name}`)}
              </div>
            )}
          </div>
        ) : (
          // Desktop view with vertical layout
          <div className={styles.desktopLayout}>
            <div className={styles.iconContainer}>
              <IconComponent size={iconSize} color={activeButton ? item.activeIconColor : item.inactiveIconColor} />
              {showRedDot && (
                <div className={styles.redDotContainer}>
                  <RedDotIcon small={false} />
                </div>
              )}
            </div>
            {!isExpandToggle && (
              <div className={styles.desktopLabel} style={textStyle}>
                {t(`menu.${item.name}`)}
              </div>
            )}
          </div>
        )}

        <div
          className={classNames(styles.speechBubble, {
            [styles.show]: showBubble,
          })}
          onClick={handleHideBubble}
        >
          {bubbleText}
        </div>
      </div>
    </MenuItemButtonWrapper>
  );
};
